import { computed, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { AuthService, CacheService } from '../../auth.service';
import { IOfferCard, ICouponUse, IPackage, IPlan, ICoupon, ITransaction } from '../../types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { ITruevo } from './components/billing-card/billing-card.component';
import { backend } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { LocationService } from '../../location.service';
import { PaymentIntent } from '@stripe/stripe-js';
import { IRedemptionCard } from '../../brands/redemption-card/redemption-card.component';

export interface IMembership {
  _id: string;
  level?: number;
  cards?: number;
  payment_methods?: number;
  package: IPackage;
  

  transaction?: string;
  base_price?: string;
  final_price?: string;
  interval?: number;

  start?: string;
  recurs?: boolean;
  expiry?: string;
  next_package?: IPackage;
  next_transaction?: string;
}

export interface IStatGroup {
  total_redemptions: number;
  total_savings: number;
  year_redemptions: number;
  year_savings: number;
  monthly_redemptions: number[];
  monthly_savings: number[];
}

export interface IProfileStats {
  _id: string;
  consumer: string;
  year: number;
  identifier: string;
  global: IStatGroup;
  offers: IStatGroup;
  vouchers: IStatGroup;
}

export interface IProfile {
  _id: string;
  status: boolean;
  email: string;
  name: string;
  surname: string;
  created: Date;
  country_code?: string;
  phone?: string;
  birthday?: string;
  gender?: string;
  country?: string;
  optin_email?: boolean;
  optin_email_remarketing?: boolean;
  optin_sms?: boolean;
  optin_sms_remarketing?: boolean;
  optin_push?: boolean;
  optin_push_remarketing?: boolean;
  optin_push_third_party?: boolean;
  optin_geo?: boolean;
  has_password?: boolean;
  has_facebook?: boolean;
  has_google?: boolean;
  has_apple?: boolean;
  membership?: IMembership;
  stats?: IProfileStats[];
  email_verified?: boolean;
  phone_verified?: boolean;
  requires_email_verified?: boolean;
}

export interface IStripeCreatedCard {
  client_secret?: string;
}

export interface IStripeAddedCard {
  method_id?: string;
  transaction?: string;
}

export interface IStripeCard {
  _id?: string;
  expiry?: string;
  card_end?: string;
  payment_method?: string;
}

export interface IHandleResponse {
  _id: string;
  status: 'STARTED' | 'ACTION_REQUIRED' | 'PAID' | 'COMPLETED' | 'FAILED';
  error?: string; 
  client_secret?: string;
  intent_status?: PaymentIntent.Status;
  payment_method?: string;
  last_payment_error?: string; 
  package?: string;
}

export interface Membership {
  _id: string;
  expiry?: string;
  recurs?: boolean;
  reminded?: string;
  consumer: string;
  level?: number;
  transaction?: string;
  start: string;
  created: string;
  package: IPackage;
  consecutiveness?: number;
  cycle_start?: string;
}

export interface ICheckup {
  consumer: IProfile;
  membership?: Membership;
  transaction?: ITransaction;
  use?: {
    _id: string;
    code: string;
    coupon: {
      _id: string;
      percentage: boolean;
      reduction: number;
    }
  };
}


@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  changed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  auth = inject(AuthService);
  toast = inject(ToastrService);
  http = inject(HttpClient);
  router = inject(LocationService);
  cache = inject(CacheService);

  profile = signal<IProfile|null>(null);
  loading = signal<boolean>(false);
  membership = computed(() => this.profile()?.membership ? Object.assign({}, this.profile()?.membership) : undefined);

  constructor() {
    this.auth.fireState$.pipe(takeUntilDestroyed(), tap(() => this.loading.set(true)), switchMap((v) => v ? this.getProfile() : of(null))).subscribe({
      next: profile => {
        if(profile){
          this.profile.set(profile);
          this.changed.next(true);
        } else {
          this.profile.set(null);
          this.changed.next(false);
        }
        this.loading.set(false);
      },
      error: err => {
        err = err.error||err;
        if (err.code == 3) {
          this.toast.error('Account deactived or deleted');
          this.router.navigate(['/logout'], { queryParams: { reason: 'Account deactivated or deleted' } });
        } else {
          this.toast.error('Failed to get profile details');
        }
        this.loading.set(false);
      }
    });
   }

  getProfile(): Observable<IProfile> {
    return this.http.get<IProfile>(backend + 'v2/profile');
  }

  getReceipts() {
    return this.http.get<ITransaction[]>(backend + 'profile/history');
  }

  cancelSubscription(membership: string): Observable<any> {
    return this.http.post<any>(backend + 'payments/toggle', { membership });
  }

  downgradeSubscription(membership: string): Observable<any> {
    return this.http.post<any>(backend + 'payments/downgrade', { membership });
  }

  toggleSubscription(membership: string, state: boolean): Observable<any> {
    return this.http.post<any>(backend + 'payments/toggle', { membership, state });
  }

  editProfile(changes: Partial<IProfile>){
    return this.http.post<{ message: string; pending_email: boolean }>(backend + 'profile/edit', changes);
  }

  getSaves(): Observable<IOfferCard[]> {
    return this.http.get<IOfferCard[]>(backend + 'profile/saves');
  }

  
  getRedemptions(skip: number = 0, limit: number = 10) {
    return this.http.get<{ redemptions: IRedemptionCard[], plans: IPlan[] }>(backend + 'profile/redemptions', { params: { skip: String(skip), limit: String(limit) }});
  }

  editRedemption(_id: string, savings: number){
    return this.http.post<{ message: string; total_savings: number; year_savings: number }>(backend + 'redemption/savings', { _id, savings });
  }

  // sendQuery(enquiry: IEnquiry): Observable<any>{
  //   return this.http.post<any>(backend + 'contact-us', enquiry);
  // }

  getTruevos(){
    return this.http.get<ITruevo[]>(backend + 'card/list');
  }

  removeTruevo(card: string){
    return this.http.post<{ message: string }>(backend + 'card/remove', { card });
  }

  getCoupons(plan: string|null = null, unusedOnly: boolean = false){
    return this.http.get<{ uses: ICouponUse[], packages: IPackage[] }>(backend + 'v2/coupon/list', { params: { ...plan ? { plan } : {}, unused: unusedOnly ? '1' : '0' } }).pipe(tap(({packages}) => {
      (packages??[]).forEach(_package => {
        if(_package.plan_details){
          this.cache.registerPlan(_package.plan_details);
        }
      });
    }));
  }

  addCoupon(coupon: string, plan: string|null = null, prompt: string|null = null) {
      return this.http.post<ICouponUse & { coupon: ICoupon & { package: IPackage; } }>(backend + 'coupon/add', { coupon, ...plan ? { plan } : {}, ...prompt ? { prompt } : {} });
  }

  getReferrer(code: string) {
    return this.http.get<{ name: string, surname: string }>(backend + 'referrer', { params: { code } });
}

  useCorporate(use_id: string, source: 'app' | 'web' = 'web'){
    return from(this.auth.getIP()).pipe(switchMap(ip => this.http.post<any>(backend + 'checkout/handle', { use_id, ip, source, bypass: true })));
  }

  prioritizeUse(use: string): Observable<ICouponUse>{
    return this.http.post<ICouponUse>(backend + 'coupon/prefer', { use });
  }

  createCouponPayment(package_id: string){
    return this.http.get<{ payment_intent: string; state: string; package: IPackage, is_gift?: boolean, email?: string, fullname?: string }>(backend + 'checkout/coupon/create', { params: { package_id } });
  }

  handleCouponPayment(payment_intent: string, data: { email: string, fullname: string, start_date?: Date; digital?: boolean; recipient?: string; recipient_email?: boolean; recipient_address?: string; message?: string; }) {
    return from(this.auth.getIP()).pipe(switchMap(ip => this.http.post<{ code: string; coupon_id?: string; coupon_name?: string; package_id: string, package_slug: string, package_name: string; price: string, transaction: string; source: string; state: string, start: Date }>(backend + 'checkout/coupon/handle', { payment_intent, ...data, ip })));
  }


  handleCheckout(package_id: string, use_id?: string, intent_id?: string, timestamp?: number, bypass: boolean = false, source: 'App' | 'Web' = 'Web'){

    const intent_type = intent_id && intent_id.startsWith('pi_') ? 'payment' : 'setup';
    return from(this.auth.getIP()).pipe(switchMap(ip => 
    this.http.post<IHandleResponse>(backend + 'checkout/handle', {
      package_id,
      use_id,
      intent_type,
      intent_id,
      timestamp,
      source,
      bypass,
      ip
    })));
  }

  //* notify backend about paymentIntent update
  handleAction(transaction_id: string){
    return from(this.auth.getIP()).pipe(switchMap(ip => 
    this.http.post<IHandleResponse>(backend + 'checkout/action', {
      transaction_id,
      ip
    })))
  }

  //* request a paymentIntnet update
  handleRetry(transaction_id: string){
    return from(this.auth.getIP()).pipe(switchMap(ip => 
    this.http.post<IHandleResponse>(backend + 'checkout/retry', {
      transaction_id,
      ip
    })))
  }

  getStats(){
    return this.http.get<ICheckup>(backend + 'profile/checkup');
  }

  updateStats(changes: Partial<IProfile>){
    return this.http.post<{ message: string }>(backend + 'profile/edit', changes);
  }

  authEmailResend(){
    return this.http.post<{ message: string }>(backend + 'auth/send-email', {});
  }

}
